import { get, post } from 'utils/apiWrapper';
import { prepareHeadersFromContext } from 'utils/helpers';
import { normalizeString } from 'utils/StringUtils';
import { STORE_ROUTE, STORES_ROUTE, BOOK_APPOINTMENT, STORES_BY_COUNTRY, STORES_BY_CITY } from 'utils/routes';
import { getDomainValue } from 'utils/domain';
import type { TGeneralActionType } from 'actions/general';

// lat 1 west lat2 east lon1 north lon2 south
export const getStoresByCoords = (
  context: any,
  lang: string,
  country_code: string,
  latitude: string,
  longitude: string,
  latitude2: string,
  longitude2: string,
) =>
  new Promise((resolve, reject) => {
    try {
      let path = `${STORES_ROUTE}?profile=${lang}&countryCode=${country_code}`;
      const [parsedLatitude, parsedLongitude] = [parseFloat(latitude), parseFloat(longitude)];

      if (latitude2 !== undefined && longitude2 !== undefined) {
        const [parsedLatitude2, parsedLongitude2] = [parseFloat(latitude2), parseFloat(longitude2)];
        path = `${path}&latitude1=${parsedLatitude}&longitude1=${parsedLongitude}&latitude2=${parsedLatitude2}&longitude2=${parsedLongitude2}`;
      } else {
        path = `${path}&latitude=${parsedLatitude}&longitude=${parsedLongitude}`;
      }

      const headers = prepareHeadersFromContext(context);

      get({ path, headers }).then((response) => {
        if (response && response.data) {
          const { data } = response;
          const { status, stores } = data;

          if (status !== 'OK') {
            return reject(response);
          }

          return resolve({ stores });
        }

        return resolve([]);
      });
    } catch (e) {
      reject(e);
    }
  });

interface TGetStore extends TGeneralActionType {
  id: string;
}

export const getStore = ({ context, profile, id }: TGetStore) =>
  new Promise((resolve, reject) => {
    const path = `${STORE_ROUTE}?profile=${profile}&storeId=${id}`;
    const headers = prepareHeadersFromContext(context);

    get({ path, headers })
      .then(({ data }) => {
        const { status, store, relatedStores } = data;
        if (status !== 'OK') {
          reject(status);
        } else if (store) {
          resolve({ ...store, relatedStores });
        } else {
          throw new Error('API returned no data for this store.');
        }
      })
      .catch(reject);
  });

export const getStoresByCountry = (context: any, lang: string, country: string, useCode: boolean = true) =>
  new Promise((resolve, reject) => {
    const path = `${STORES_ROUTE}?profile=${lang}&${useCode ? `countryCode=${country}` : `country=${normalizeString(country)}`}`;
    const headers = prepareHeadersFromContext(context);

    get({ path, headers })
      .then((response) => {
        if (response && response.data) {
          const { status, stores, cities } = response.data;

          if (status !== 'OK') {
            return reject(response);
          }

          return resolve({ stores, cities });
        }

        return reject(response);
      })
      .catch(reject);
  });

interface TGetAllStores extends TGeneralActionType {
  repairProgram: boolean;
}

export const getAllStores = ({ context, profile, repairProgram }: TGetAllStores) =>
  new Promise((resolve, reject) => {
    const queryParams = new URLSearchParams({ profile });
    if (repairProgram) {
      queryParams.set('repairProgram', 'true');
    }
    const path = `${STORES_ROUTE}?${queryParams.toString()}`;
    const headers = prepareHeadersFromContext(context);
    get({ path, headers })
      .then(({ data }) => {
        const { status, stores } = data;
        if (status !== 'OK') {
          reject(status);
        } else if (Array.isArray(stores)) {
          resolve(stores);
        } else {
          console.error(`API returned no data for stores ${process.env.API_GATEWAY + path}`);
          return [];
        }
      })
      .catch(reject);
  });

export const getAllStoresByCountry = (profile: string) =>
  new Promise((resolve, reject) => {
    const path = `${STORES_BY_COUNTRY}?profile=${profile}`;

    get({ path })
      .then(({ data }) => {
        const { countries } = data;
        if (!countries) {
          reject('KO');
        } else if (Array.isArray(countries)) {
          resolve(countries);
        } else {
          throw new Error('API returned no data for this store.');
        }
      })
      .catch(reject);
  });

export const getAllStoresByCity = (profile: string, countryCode: string) =>
  new Promise((resolve, reject) => {
    const path = `${STORES_BY_CITY}?profile=${profile}&countryCode=${countryCode}`;

    get({ path })
      .then(({ data }) => {
        const { cities } = data;
        if (!cities) {
          reject('KO');
        } else if (Array.isArray(cities)) {
          resolve(cities);
        } else {
          throw new Error('API returned no data for this store.');
        }
      })
      .catch(reject);
  });

type TAppointmentData = {
  email: string;
  informationDate: any;
  name: string;
  storeCode: string;
  telephone: string;
  videocallAppointment: boolean;
  captchaToken: string;
};

export const bookAppointment = (lang: string, appointmentData: TAppointmentData) =>
  new Promise((resolve, reject) => {
    const path = BOOK_APPOINTMENT;
    const { email, informationDate, name, storeCode, telephone, videocallAppointment, captchaToken } = appointmentData;
    const lab = getDomainValue({ camper: false, camperlab: true, nnormal: false });
    const additionalData = {
      email,
      informationDate,
      name,
      storeCode,
      telephone,
      videocallAppointment,
      captchaToken,
      lab,
    };

    post({
      path,
      data: {
        profile: lang,
        ...additionalData,
      },
    })
      .then((response) => {
        if (response && response.data) {
          const { status } = response.data;
          if (status !== 'OK') {
            const error = response.data?.errors?.length ? `${response.data?.errors[0]?.code}: ${response.data?.errors[0]?.message}` : status;
            reject(error);
          } else {
            resolve(true);
          }
        }
      })
      .catch(reject);
  });
